/* eslint-disable camelcase */
import TransportationClient from '@/api/TransportationClient';

// Helper function to determine a transportation address's phone
const getAddressPhone = async (address, dealerId, apiInstance) => {
  if (address?.phone) {
    return address.phone;
  }
  if (address?.transportationPrimaryContact?.phone) {
    return address.transportationPrimaryContact.phone;
  }

  const { data } = await apiInstance.getTransportContactByDealer(dealerId);

  return data.data.phone;
};

const defaultState = {
  pickupAddress: {},
  deliveryAddress: {},
  quoteInfo: {},
  currentDealership: {},
  newOrderInfo: {},
  closeWithoutPrompt: true,
  requestId: null,
  inop: false,
  MAX_VEHICLES: 20,
  gatePassKeys: new Map(),
  pickupAndDeliveryDefaultZipCodes: {},
};

const mutations = {
  setQuoteInfo(state, quoteData) {
    state.quoteInfo = quoteData;
  },
  setPickupAddress(state, pickup) {
    state.pickupAddress = pickup;
  },
  setDeliveryAddress(state, delivery) {
    state.deliveryAddress = delivery;
  },
  setCurrentDealership(state, dealership) {
    state.currentDealership = dealership;
  },
  setNewOrderInfo(state, orderInfo) {
    state.newOrderInfo = orderInfo;
  },
  resetState(state) {
    // Reset the entire state object
    state.gatePassKeys.clear();
    Object.assign(state, defaultState);
  },
  setCloseWithoutPrompt(state, promptState) {
    state.closeWithoutPrompt = promptState;
  },
  setInop(state, value) {
    state.inop = value;
  },
  setRequestId(state, value) {
    state.requestId = value;
  },
  setGatePassKey(state, { vin, fileKey, fileObj }) {
    state.gatePassKeys.set(vin, { fileKey, fileObj });
  },
  setDeleteGatePassKey(state, { vin }) {
    state.gatePassKeys.delete(vin);
  },
  setPickupAndDeliveryDefaultZipCodes(state, value) {
    state.pickupAndDeliveryDefaultZipCodes = value;
  },
};

const actions = {
  async getQuote({ rootGetters, commit }, payload) {
    const api = new TransportationClient(rootGetters.currentUser);
    const { data } = await api.getTransportationQuote(payload);

    // TODO: Should set just what we need and not the whole response
    commit(
      'setQuoteInfo',
      data.data,
    );
    commit('setPickupAddress', {
      ...data.data.pickup,
      zip: payload.pickup_zip,
      ...(payload.pickup_address.zipCode && {
        street: payload.pickup_address.street,
        phone: await getAddressPhone(payload.pickup_address, payload.dealer_id, api),
        instructions: payload.pickup_address.note,
      }
      ),
    });
    commit('setDeliveryAddress', {
      ...data.data.delivery,
      zip: payload.delivery_zip,
      ...(payload.delivery_address.zipCode && {
        street: payload.delivery_address.street,
        phone: await getAddressPhone(payload.delivery_address, payload.dealer_id, api),
        instructions: payload.delivery_address.note,
      }
      ),
    });
    commit('setCurrentDealership', payload.dealership);
    commit('setInop', payload.inop);

    return data;
  },
  async createRequest({ rootGetters, commit }, payload) {
    const api = new TransportationClient(rootGetters.currentUser);
    const { data } = await api.createTransportationRequest(payload);
    // eslint-disable-next-line vue/max-len
    // workaround because we the backend response return an object or an array depending if it is multi vehicle
    if (data.data.length === 1) {
      commit('setRequestId', data.data[0].request_id);
      return data;
    }

    commit('setRequestId', data.data.request_id);

    return data;
  },
  resetState({ commit }) {
    commit('resetState');
  },
  async addGatePass({ rootGetters, commit }, { vin, fileObj }) {
    const { file } = fileObj[0];

    const api = new TransportationClient(rootGetters.currentUser);

    const { data } = await api.uploadGatePass({ file });

    const { fileKey } = data.data;

    commit('setGatePassKey', { vin, fileKey, fileObj });
  },
  removeGatePass({ commit }, { vin }) {
    commit('setDeleteGatePassKey', { vin });
  },
  addPickupAndDeliveryDefaultZipCodes({ commit }, zipCodes) {
    commit('setPickupAndDeliveryDefaultZipCodes', zipCodes);
  },
};

const getters = {
  getNewOrderInfo: (state) => () => state.newOrderInfo,
  getQuoteInfo: (state) => () => state.quoteInfo,
};

export default {
  state: defaultState,
  mutations,
  actions,
  getters,
};

export { getAddressPhone };
